import React from 'react'
import styled, { css } from 'styled-components'
import { Link } from '@reach/router'

// icons
import { ClipboardList } from '@styled-icons/fa-solid/ClipboardList'
import { PeopleFill } from '@styled-icons/bootstrap'
import { Settings } from 'styled-icons/ionicons-outline'
import { Question } from '@styled-icons/evil'

// utils
import th from 'utils/themeHelper'

// styles
import DashCardWrapper from './DashCardWrapper'

// icons
import { AdminPanelSettings } from '@styled-icons/material/AdminPanelSettings'
import { PeopleFill as People } from '@styled-icons/bootstrap'
import { Pencil } from '@styled-icons/fa-solid/Pencil'

const baseIconStyles = css`
  background: ${props => props.background};
  border-radius: ${props => (props.background ? '50%' : '0')};
  color: ${th('borders.dark')};
  height: 20px;
  padding: 2px;
  width: 20px;
`

const AdminPanelIcon = styled(AdminPanelSettings)`
  ${baseIconStyles}
`

const PeopleIcon = styled(People)`
  ${baseIconStyles}
`

const ActionItem = styled.div`
  margin-right: 10px;
  margin-top: 15px;

  &:last-of-type {
    margin-right: 0;
  }
`

const Actions = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: flex-start;
  margin: 10px;
`

const ActionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const TeamCardLink = styled(Link)`
  border-color: ${th('backgrounds.mid')};
  border-radius: 4px;
  border-style: solid;
  border-width: 1px;
  color: ${th('text.dark')};
  margin: 0;
  padding: 8px 12px;
  text-align: center;
  text-decoration: none;
  white-space: nowrap;

  & > svg {
    width: 15px;
    margin-right: 10px;
  }

  @media screen and (max-width: 400px) {
    padding: 6px 8px;
    font-size: 12px;

    & > svg {
      width: 12px;
      margin-right: 6px;
    }
  }
`

const ExternalLink = styled.a`
  border-color: ${th('backgrounds.mid')};
  border-radius: 4px;
  border-style: solid;
  border-width: 1px;
  color: ${th('text.dark')};
  margin: 0;
  padding: 8px 12px;
  text-align: center;
  text-decoration: none;
  white-space: nowrap;

  & > svg {
    width: 15px;
    margin-right: 10px;
  }

  @media screen and (max-width: 400px) {
    padding: 6px 8px;
    font-size: 12px;

    & > svg {
      width: 12px;
      margin-right: 6px;
    }
  }
`

const Info = styled.div`
  display: block;
  margin: 10px 15px 0;
  flex: 1;
`

const InfoFieldLabel = styled.div`
  font-family: ${th('fonts.bold')};
  margin-right: 10px;
`

const InfoFieldValue = styled.div`
  font-family: ${th('fonts.regular')};
  margin-left: 30px;
`

const InfoFieldIcon = styled.div`
  margin-right: 10px;

  & > svg {
    height: 20px;
  }
`

const InfoGroup = styled.div`
  min-width: 30%;
`

const InfoGroupRow = styled.div`
  color: ${th('text.mid')};
  line-height: 1.25em;
  margin-bottom: 15px;
`

const InfoGroupRowInner = styled.div`
  display: flex;
  word-break: break-word;
`

const isDev = import.meta.env.DEV

const DashFormCard = ({ id, name, creator_name, responses_count, questions_count, shortcode }) => {
  const formPath = isDev
    ? `http://localhost:5173/${shortcode}`
    : `https://forms.quicksticks.io/${shortcode}`

  return (
    <DashCardWrapper>
      <ActionsWrapper>
        <Info>
          <InfoGroup>
            <InfoGroupRow>
              <InfoGroupRowInner>
                <InfoFieldIcon>
                  <ClipboardList />
                </InfoFieldIcon>
                <InfoFieldLabel>Form name</InfoFieldLabel>
              </InfoGroupRowInner>

              <InfoGroupRowInner>
                <InfoFieldValue>{name}</InfoFieldValue>
              </InfoGroupRowInner>
            </InfoGroupRow>

            <InfoGroupRow>
              <InfoGroupRowInner>
                <InfoFieldIcon>
                  <Question background="gold" />
                </InfoFieldIcon>
                <InfoFieldLabel>Questions</InfoFieldLabel>
              </InfoGroupRowInner>

              <InfoGroupRowInner>
                <InfoFieldValue>{questions_count}</InfoFieldValue>
              </InfoGroupRowInner>
            </InfoGroupRow>

            <InfoGroupRow>
              <InfoGroupRowInner>
                <InfoFieldIcon>
                  <AdminPanelIcon background="#379b7550" />
                </InfoFieldIcon>
                <InfoFieldLabel>Creator</InfoFieldLabel>
              </InfoGroupRowInner>

              <InfoGroupRowInner>
                <InfoFieldValue>{creator_name}</InfoFieldValue>
              </InfoGroupRowInner>
            </InfoGroupRow>

            <InfoGroupRow>
              <InfoGroupRowInner>
                <InfoFieldIcon>
                  <PeopleIcon />
                </InfoFieldIcon>
                <InfoFieldLabel>Responses</InfoFieldLabel>
              </InfoGroupRowInner>

              <InfoGroupRowInner>
                <InfoFieldValue>{responses_count}</InfoFieldValue>
              </InfoGroupRowInner>
            </InfoGroupRow>
          </InfoGroup>
        </Info>

        <Actions>
          <ActionItem>
            <TeamCardLink to={`/dashboard/partners/${id}`}>
              <Settings />
              <span>Edit</span>
            </TeamCardLink>
          </ActionItem>

          <ActionItem>
            <ExternalLink href={formPath}>
              <Pencil />
              <span>View form</span>
            </ExternalLink>
          </ActionItem>

          <ActionItem>
            <TeamCardLink to={`/dashboard/forms/${id}/responses`}>
              <PeopleFill />
              <span>View responses</span>
            </TeamCardLink>
          </ActionItem>
        </Actions>
      </ActionsWrapper>
    </DashCardWrapper>
  )
}

export default DashFormCard
